/**
 * useLiffLineUid — 公開ページ用：LIFF SDK を初期化して lineUid を取得。
 *
 * 動作:
 *   1. Firestore settings/liff から LIFF ID 取得（公開読取ルール）→ 環境変数 fallback
 *   2. liff.init → liff.isInClient() なら getProfile().userId を返却
 *   3. LIFF 未設定 / 外部ブラウザ / 失敗時は null を返す（呼出側はフォールバック動作）
 *
 * これにより LINE トーク内で LP を開いた人の lineUid を audience_events に補完でき、
 * LINE → LP のタイムラインを個人粒度で追えるようになる。
 *
 * 既存利用: LiffFormPage が同等ロジックを持つ。本フックは LP 用の薄い抽出版。
 */
import liff from '@line/liff';
import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';

import type { SettingsLiff } from '@goenhub/shared';

import { db } from '@/lib/firebase';

const LIFF_ID_FALLBACK = import.meta.env.VITE_LIFF_ID as string | undefined;

async function resolveLiffId(): Promise<string | null> {
  try {
    const snap = await getDoc(doc(db, 'settings', 'liff'));
    if (snap.exists()) {
      const data = snap.data() as Partial<SettingsLiff>;
      if (typeof data.liffId === 'string' && data.liffId.trim()) {
        return data.liffId.trim();
      }
    }
  } catch {
    // フォールバックへ
  }
  return LIFF_ID_FALLBACK ?? null;
}

interface LiffLineUidResult {
  /** 解決済みかどうか（true になるまで recordLandingView 等は待つ） */
  ready: boolean;
  /** 取得できた lineUid（LINE 外ブラウザ / LIFF 未設定 / 失敗時は null） */
  lineUid: string | null;
  /** LINE 内蔵ブラウザかどうか（UI 分岐に使えると便利） */
  isInClient: boolean;
}

/**
 * @param skip true なら LIFF 初期化を行わず即座に ready=true / lineUid=null を返す。
 *   URL に既に ?lid= が含まれている等、LIFF 不要な場合に呼出側で短絡できる。
 */
export function useLiffLineUid(skip = false): LiffLineUidResult {
  const [state, setState] = useState<LiffLineUidResult>({
    ready: skip,
    lineUid: null,
    isInClient: false,
  });

  useEffect(() => {
    if (skip) {
      setState({ ready: true, lineUid: null, isInClient: false });
      return;
    }
    let cancelled = false;
    (async () => {
      const liffId = await resolveLiffId();
      if (cancelled) return;
      if (!liffId) {
        setState({ ready: true, lineUid: null, isInClient: false });
        return;
      }
      try {
        await liff.init({ liffId });
        if (cancelled) return;
        if (liff.isInClient()) {
          try {
            const profile = await liff.getProfile();
            if (cancelled) return;
            setState({
              ready: true,
              lineUid: profile.userId ?? null,
              isInClient: true,
            });
          } catch {
            setState({ ready: true, lineUid: null, isInClient: true });
          }
        } else {
          setState({ ready: true, lineUid: null, isInClient: false });
        }
      } catch {
        if (cancelled) return;
        setState({ ready: true, lineUid: null, isInClient: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [skip]);

  return state;
}
